import { useLanguage } from '@/contexts/LanguageContext';
import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Calculator, ChevronUp } from 'lucide-react';
import {
  useCalculator, PopularChips, VehicleCards, TrustBadges,
  PriceResult, AZFilter, DestinationDropdown, CTAButtons,
  VEHICLE_CONFIG, type VehicleType,
} from './CalculatorShared';
import { destinations } from '@/data/destinations';

interface Props { onInteraction: () => void; onConversion: () => void; }

// Mini sticky calculator
const StickyMini = ({ onInteraction, onConversion }: Props) => {
  const { t } = useLanguage();
  const [visible, setVisible] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const calc = useCalculator(onInteraction);

  useEffect(() => {
    const handler = () => {
      const el = document.getElementById('calculator-main');
      if (el) {
        const rect = el.getBoundingClientRect();
        setVisible(rect.bottom < 0);
      }
    };
    window.addEventListener('scroll', handler, { passive: true });
    return () => window.removeEventListener('scroll', handler);
  }, []);

  if (!visible) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ y: 100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 100, opacity: 0 }}
        className="fixed bottom-0 left-0 right-0 z-40 md:bottom-4 md:right-4 md:left-auto md:w-96"
        data-ab-variant="C-sticky"
      >
        <div className="bg-card border-t md:border border-border md:rounded-2xl shadow-xl p-4 space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm font-bold text-foreground flex items-center gap-2">
              <Calculator className="w-4 h-4 text-primary" /> Preisrechner
            </span>
            <button onClick={() => setExpanded(!expanded)} className="p-1 text-muted-foreground hover:text-foreground">
              <ChevronUp className={`w-4 h-4 transition-transform ${expanded ? '' : 'rotate-180'}`} />
            </button>
          </div>

          {expanded && (
            <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} className="space-y-3">
              <DestinationDropdown
                isOpen={calc.isOpen} selected={calc.selected} grouped={calc.grouped}
                dropdownRef={calc.dropdownRef as React.RefObject<HTMLDivElement>}
                inputRef={calc.inputRef as React.RefObject<HTMLInputElement>}
                query={calc.query} setQuery={calc.setQuery} setSelected={calc.setSelected as any}
                setIsOpen={calc.setIsOpen} setActiveLetter={calc.setActiveLetter}
                handleSelect={calc.handleSelect} handleClear={calc.handleClear}
                onInteraction={onInteraction}
              />
              <VehicleCards vehicleType={calc.vehicleType} setVehicleType={calc.setVehicleType} compact />
              {calc.selected && calc.selected.km > 0 && (
                <PriceResult selected={calc.selected} finalPrice={calc.finalPrice} basePrice={calc.basePrice} multiplier={calc.multiplier} km={calc.selected.km} />
              )}
              <div onClick={onConversion}>
                <CTAButtons />
              </div>
            </motion.div>
          )}

          {!expanded && (
            <button
              onClick={() => setExpanded(true)}
              className="w-full py-2.5 rounded-xl bg-gradient-gold text-primary-foreground font-semibold text-sm min-h-[44px]"
            >
              Jetzt Preis berechnen
            </button>
          )}
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

// Main full calculator
const VariantC = ({ onInteraction, onConversion }: Props) => {
  const { t } = useLanguage();
  const calc = useCalculator(onInteraction);

  return (
    <>
      <section id="calculator" className="py-20 md:py-28 bg-background">
        <div id="calculator-main" className="container">
          <div className="text-center mb-10">
            <h2 className="font-display text-3xl md:text-4xl font-bold text-foreground mb-4">{t.calculator.title}</h2>
            <p className="text-muted-foreground text-lg max-w-xl mx-auto">{t.calculator.subtitle}</p>
            <div className="w-16 h-1 bg-gradient-gold mx-auto mt-6 rounded-full" />
          </div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-2xl mx-auto bg-card rounded-2xl shadow-card border border-border p-6 md:p-8"
            data-ab-variant="C"
          >
            <div className="mb-5">
              <p className="text-xs font-semibold text-muted-foreground mb-2 uppercase tracking-wide">{t.calculator.popularDestinations}</p>
              <PopularChips onChipClick={calc.handleChipClick} />
            </div>

            <div className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">{t.calculator.azFilter}</label>
                <AZFilter activeLetter={calc.activeLetter} availableLetters={calc.availableLetters} onLetterClick={calc.handleLetterClick} />
              </div>

              <DestinationDropdown
                isOpen={calc.isOpen} selected={calc.selected} grouped={calc.grouped}
                dropdownRef={calc.dropdownRef as React.RefObject<HTMLDivElement>}
                inputRef={calc.inputRef as React.RefObject<HTMLInputElement>}
                query={calc.query} setQuery={calc.setQuery} setSelected={calc.setSelected as any}
                setIsOpen={calc.setIsOpen} setActiveLetter={calc.setActiveLetter}
                handleSelect={calc.handleSelect} handleClear={calc.handleClear}
                onInteraction={onInteraction}
              />

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">{t.calculator.vehicleType}</label>
                <VehicleCards vehicleType={calc.vehicleType} setVehicleType={calc.setVehicleType} />
              </div>
            </div>

            {calc.selected && calc.selected.km > 0 && (
              <div className="mt-6">
                <PriceResult selected={calc.selected} finalPrice={calc.finalPrice} basePrice={calc.basePrice} multiplier={calc.multiplier} km={calc.selected.km} />
              </div>
            )}

            <TrustBadges className="mt-5" />

            <div className="mt-5" onClick={onConversion}>
              <CTAButtons />
            </div>
          </motion.div>
        </div>
      </section>

      <StickyMini onInteraction={onInteraction} onConversion={onConversion} />
    </>
  );
};

export default VariantC;
